import { useAppSelector } from "../state/hooks";
import { selectUser } from "../state/selectors";
import type { PermissionKey } from "../services/types/ComponentTypes";
import type { ModuleLevelPermission, UserLevelPermission } from "../types/user";
import { decryptUserData, loadEncryptedUserData } from "../state/utils/configUtils";

/** Module_Id for the Timeline Access rights category */
export const TIMELINE_MODULE_ID = "11";

/** Screen ids under Timeline (View = access) */
export const TIMELINE_SCREEN_IDS = {
  inspection: "2300",
  vehicle: "2301",
  customer: "2302",
} as const;

export type TimelineSurface = keyof typeof TIMELINE_SCREEN_IDS;

/**
 * Timeline module visibility from login modulelevelpermissions.
 * Uses effective_permission (user > group > server). Missing "11" → deny.
 */
export function checkTimelineModulePermission(
  modulePermissions:
    | Record<string, Pick<ModuleLevelPermission, "effective_permission"> | undefined>
    | null
    | undefined
): boolean {
  const entry = modulePermissions?.[TIMELINE_MODULE_ID];
  if (!entry) {
    return false;
  }
  return entry.effective_permission !== 1;
}

/**
 * Per-surface Timeline access (Inspection / Vehicle / Customer).
 * Requires Timeline module on, then View when a screen permission row exists.
 * Missing screen row → allowed (module on, View not customized yet).
 */
export function checkTimelineScreenPermission(
  surface: TimelineSurface,
  modulePermissions:
    | Record<string, Pick<ModuleLevelPermission, "effective_permission"> | undefined>
    | null
    | undefined,
  screenPermissions: UserLevelPermission[] | null | undefined
): boolean {
  if (!checkTimelineModulePermission(modulePermissions)) {
    return false;
  }

  const screenId = TIMELINE_SCREEN_IDS[surface];
  const entry = (screenPermissions ?? []).find(
    (p) => String(p.ScreenId) === String(screenId)
  );
  if (!entry) {
    return true;
  }
  // Missing View on older rows → allow; only explicit false denies
  if (entry.View === undefined || entry.View === null) {
    return true;
  }
  return Boolean(entry.View);
}

/** Session helpers for non-hook callers (e.g. TourContext). */
export function hasTimelinePermissionFromSession(
  surface: TimelineSurface = "inspection"
): boolean {
  try {
    if (typeof window === "undefined") return false;
    const encryptedUserData = loadEncryptedUserData();
    if (encryptedUserData) {
      const decryptedData = decryptUserData(encryptedUserData);
      return checkTimelineScreenPermission(
        surface,
        decryptedData?.modulelevelpermissions,
        decryptedData?.userlevelpermissions
      );
    }
  } catch (error) {
    console.error("Error checking timeline permission from session:", error);
  }
  return false;
}

/** @deprecated Prefer checkTimelineScreenPermission / hasTimelinePermission(surface) */
export function checkTimelinePermission(
  modulePermissions:
    | Record<string, Pick<ModuleLevelPermission, "effective_permission"> | undefined>
    | null
    | undefined
): boolean {
  return checkTimelineModulePermission(modulePermissions);
}

/**
 * Returns a helper that checks whether the logged-in user has a specific
 * permission for a given screen.
 *
 * Usage:
 *   const { hasPermission } = usePermissions();
 *   hasPermission("104", "Add") // true | false
 *
 * Rules:
 *  - Returns `true` (permissive) when no screenId is given.
 *  - Returns `true` when no matching entry is found in userlevelpermissions.
 *  - Returns the stored boolean value otherwise.
 */
export function usePermissions() {
  const user = useAppSelector(selectUser);
  const permissions = user?.userlevelpermissions ?? [];
  const modulePermissions = user?.modulelevelpermissions ?? {};

  const hasPermission = (screenId: string | undefined, key: PermissionKey): boolean => {
    if (!screenId) return true;

    const entry = permissions.find((p) => String(p.ScreenId) === String(screenId));
    if (!entry) return true; // no entry → permissive default

    return Boolean(entry[key]);
  };

  const hasModulePermission = (moduleId: string | undefined): boolean => {
    if (!moduleId) return true;

    const entry = modulePermissions[moduleId];
    if (!entry) return true; // if no specific entry is found, we can default to true

    // If effective_permission is 1, hide it from UI (return false)
    return entry.effective_permission !== 1;
  };

  const hasTimelineModulePermission = (): boolean =>
    checkTimelineModulePermission(modulePermissions);

  const hasTimelinePermission = (surface: TimelineSurface = "inspection"): boolean =>
    checkTimelineScreenPermission(surface, modulePermissions, permissions);

  return {
    hasPermission,
    hasModulePermission,
    hasTimelineModulePermission,
    hasTimelinePermission,
    permissions,
    modulePermissions,
  };
}
